/**
 * Enhanced SuperAdmin Dashboard with Advanced Features
 * - Analytics Dashboard
 * - Audit Logs
 * - System Health Monitoring
 * - Bulk Actions
 * - Dark Mode Toggle
 * - Recent Logins
 * - Activity Timeline
 */

import React, { useState, useEffect } from 'react';
import {
    Building2, Users, MessageSquare, Activity, TrendingUp, RefreshCw,
    AlertCircle, Loader2, Clock, Shield, CheckCircle, XCircle,
    Moon, Sun, ChevronDown, MoreVertical, Calendar, Zap, Database,
    Cpu, HardDrive, Wifi, Bell, UserCheck, LogIn
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import superAdminService from '../../services/superAdminService';
import { logger } from '../../utils/logger';

// Stat Card Component
function StatCard({ title, value, icon: Icon, gradient, subtext, trend }) {
    return (
        <div className="bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600 font-medium">{title}</p>
                    <p className="text-3xl font-bold mt-2 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                        {value}
                    </p>
                    {subtext && (
                        <p className="text-xs text-gray-500 mt-1">{subtext}</p>
                    )}
                    {trend && (
                        <div className={cn(
                            "text-xs mt-2 flex items-center gap-1",
                            trend > 0 ? "text-green-600" : "text-red-600"
                        )}>
                            <TrendingUp className="h-3 w-3" />
                            {Math.abs(trend)}% vs last month
                        </div>
                    )}
                </div>
                <div className={cn(
                    "h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg",
                    gradient
                )}>
                    <Icon className="h-7 w-7 text-white" />
                </div>
            </div>
        </div>
    );
}

// Icon mapping for system health
const iconMap = {
    Database: Database,
    Cpu: Cpu,
    HardDrive: HardDrive,
    Wifi: Wifi
};

// System Health Card
function SystemHealthCard({ name, status, value, icon }) {
    const isHealthy = status === 'healthy';
    // Get the icon component from the map, or use a default
    const Icon = typeof icon === 'string' ? (iconMap[icon] || Database) : icon;
    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
                <div className={cn(
                    "h-10 w-10 rounded-lg flex items-center justify-center",
                    isHealthy ? "bg-green-100" : status === 'degraded' ? "bg-yellow-100" : "bg-red-100"
                )}>
                    <Icon className={cn(
                        "h-5 w-5",
                        isHealthy ? "text-green-600" : status === 'degraded' ? "text-yellow-600" : "text-red-600"
                    )} />
                </div>
                <div>
                    <p className="font-medium text-sm">{name}</p>
                    <p className="text-xs text-gray-500">{value}</p>
                </div>
            </div>
            {isHealthy ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
            ) : status === 'degraded' ? (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
            ) : (
                <XCircle className="h-5 w-5 text-red-600" />
            )}
        </div>
    );
}

// Activity Timeline Item
function ActivityItem({ activity }) {
    const getIcon = (type) => {
        switch (type) {
            case 'login': return LogIn;
            case 'create': return Building2;
            case 'update': return RefreshCw;
            case 'delete': return XCircle;
            default: return Activity;
        }
    };

    const Icon = getIcon(activity.type);
    const colors = {
        login: 'bg-blue-100 text-blue-600',
        create: 'bg-green-100 text-green-600',
        update: 'bg-yellow-100 text-yellow-600',
        delete: 'bg-red-100 text-red-600'
    };

    return (
        <div className="flex gap-3 pb-4">
            <div className={cn("h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0", colors[activity.type] || 'bg-gray-100')}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {activity.time}
                </p>
            </div>
        </div>
    );
}

// Recent Login Item
function RecentLoginItem({ login }) {
    return (
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[var(--g6-primary)] to-[var(--g6-primary-dark)] flex items-center justify-center text-white font-bold">
                    {login.username.charAt(0).toUpperCase()}
                </div>
                <div>
                    <p className="text-sm font-medium">{login.username}</p>
                    <p className="text-xs text-gray-500">{login.role}</p>
                </div>
            </div>
            <div className="text-right">
                <p className="text-xs text-gray-600">{login.time}</p>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <Wifi className="h-3 w-3" />
                    {login.ip}
                </div>
            </div>
        </div>
    );
}

// Helper: Create default stats object
const createDefaultStats = () => ({
    total_tenants: 0,
    active_tenants: 0,
    total_users: 0,
    total_sessions: 0,
    total_messages: 0,
    tenants_trend: null,
    users_trend: null,
    sessions_trend: null
});

// Helper: Create stats object from API data
const createStatsFromData = (data) => ({
    total_tenants: data.total_tenants || 0,
    active_tenants: data.active_tenants || 0,
    total_users: data.total_users || 0,
    total_sessions: data.total_sessions || 0,
    total_messages: data.total_messages || 0,
    tenants_trend: null,
    users_trend: null,
    sessions_trend: null
});

// Helper: Create default health data
const createDefaultHealthData = () => [
    { name: 'Database', status: 'unknown', value: 'Unable to check', icon: 'Database' },
    { name: 'API Server', status: 'unknown', value: 'Unable to check', icon: 'Cpu' },
    { name: 'Storage', status: 'unknown', value: 'Unable to check', icon: 'HardDrive' },
    { name: 'Network', status: 'unknown', value: 'Unable to check', icon: 'Wifi' }
];

// Custom hook for fetching stats
const useFetchStats = (setStats) => {
    return async () => {
        try {
            const data = await superAdminService.getStats();
            setStats(createStatsFromData(data));
        } catch (error) {
            logger.error('Error fetching stats:', error);
            // Set empty stats on error
            setStats({
                total_tenants: 0,
                active_tenants: 0,
                total_users: 0,
                total_sessions: 0,
                total_messages: 0,
                tenants_trend: null,
                users_trend: null,
                sessions_trend: null
            });
        }
    };
};

// Custom hook for fetching system health
const useFetchSystemHealth = (setSystemHealth, setOverallHealthStatus) => {
    return async () => {
        try {
            const data = await superAdminService.getSystemHealth();
            if (data.success && data.health) {
                setSystemHealth(data.health);
                setOverallHealthStatus(data.overall_status || 'healthy');
            }
        } catch (error) {
            logger.error('Error fetching system health:', error);
            // Set default health data on error
            setSystemHealth([
                { name: 'Database', status: 'unknown', value: 'Unable to check', icon: 'Database' },
                { name: 'API Server', status: 'unknown', value: 'Unable to check', icon: 'Cpu' },
                { name: 'Storage', status: 'unknown', value: 'Unable to check', icon: 'HardDrive' },
                { name: 'Network', status: 'unknown', value: 'Unable to check', icon: 'Wifi' }
            ]);
        }
    };
};

// Custom hook for fetching recent logins
const useFetchRecentLogins = (setRecentLogins) => {
    return async () => {
        try {
            const data = await superAdminService.getRecentLogins(5);
            if (data.success && data.logins) {
                setRecentLogins(data.logins);
            }
        } catch (error) {
            logger.error('Error fetching recent logins:', error);
            setRecentLogins([]);
        }
    };
};

// Custom hook for fetching activity timeline
const useFetchActivityTimeline = (setRecentActivities) => {
    return async () => {
        try {
            const data = await superAdminService.getActivityTimeline(5);
            if (data.success && data.activities) {
                setRecentActivities(data.activities);
            }
        } catch (error) {
            logger.error('Error fetching activity timeline:', error);
            setRecentActivities([]);
        }
    };
};

// Loading Screen Component
function LoadingScreen() {
    return (
        <div className="flex items-center justify-center h-96">
            <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-[var(--g6-secondary)] mx-auto mb-4" />
                <p className="text-gray-600">Loading dashboard...</p>
            </div>
        </div>
    );
}

// Dashboard Header Component
function DashboardHeader({ darkMode, setDarkMode, refreshing, onRefresh }) {
    return (
        <div className="flex items-center justify-between">
            <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-[var(--g6-primary)] to-[var(--g6-secondary)] bg-clip-text text-transparent">
                    Platform Dashboard
                </h1>
                <p className="text-gray-600 mt-1">Real-time analytics and system monitoring</p>
            </div>
            <div className="flex items-center gap-3">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setDarkMode(!darkMode)}
                    title="Toggle dark mode"
                >
                    {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={onRefresh}
                    disabled={refreshing}
                >
                    <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                </Button>
                <Button className="bg-gradient-to-r from-[var(--g6-primary)] to-[var(--g6-primary-dark)] text-white">
                    <Bell className="h-4 w-4 mr-2" />
                    Notifications
                </Button>
            </div>
        </div>
    );
}

// Stats Grid Component
function StatsGrid({ stats }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
                title="Total Tenants"
                value={stats.total_tenants}
                icon={Building2}
                gradient="bg-gradient-to-br from-blue-500 to-blue-600"
                subtext={`${stats.active_tenants} active`}
                trend={stats.tenants_trend}
            />
            <StatCard
                title="Total Users"
                value={stats.total_users.toLocaleString()}
                icon={Users}
                gradient="bg-gradient-to-br from-green-500 to-green-600"
                trend={stats.users_trend}
            />
            <StatCard
                title="Active Sessions"
                value={stats.total_sessions.toLocaleString()}
                icon={Activity}
                gradient="bg-gradient-to-br from-purple-500 to-purple-600"
                trend={stats.sessions_trend}
            />
            <StatCard
                title="Total Messages"
                value={stats.total_messages.toLocaleString()}
                icon={MessageSquare}
                gradient="bg-gradient-to-br from-[var(--g6-primary)] to-[var(--g6-primary-dark)]"
            />
        </div>
    );
}

// System Health Status Component
function SystemHealthStatus({ status }) {
    const statusConfig = {
        healthy: {
            color: 'text-green-600',
            bgColor: 'bg-green-600',
            message: 'All Systems Operational'
        },
        degraded: {
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-600',
            message: 'Some Services Degraded'
        },
        critical: {
            color: 'text-red-600',
            bgColor: 'bg-red-600',
            message: 'System Issues Detected'
        }
    };

    const config = statusConfig[status] || statusConfig.healthy;

    return (
        <div className={cn("flex items-center gap-2 text-sm font-medium", config.color)}>
            <div className={cn("h-2 w-2 rounded-full animate-pulse", config.bgColor)}></div>
            {config.message}
        </div>
    );
}

// System Health Section Component
function SystemHealthSection({ systemHealth, overallHealthStatus }) {
    return (
        <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-[var(--g6-primary)]" />
                    <h3 className="font-bold text-lg">System Health</h3>
                </div>
                <SystemHealthStatus status={overallHealthStatus} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {systemHealth.length > 0 ? (
                    systemHealth.map((system, index) => (
                        <SystemHealthCard key={index} {...system} />
                    ))
                ) : (
                    <div className="col-span-4 text-center text-gray-500 py-4">
                        Loading health metrics...
                    </div>
                )}
            </div>
        </div>
    );
}

// Activity Timeline Section Component
function ActivityTimelineSection({ recentActivities }) {
    return (
        <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[var(--g6-secondary)]" />
                    <h3 className="font-bold text-lg">Activity Timeline</h3>
                </div>
                <Button variant="ghost" size="sm">
                    View All
                </Button>
            </div>
            <div className="space-y-4">
                {recentActivities.length > 0 ? (
                    recentActivities.map((activity, index) => (
                        <ActivityItem key={index} activity={activity} />
                    ))
                ) : (
                    <div className="text-center text-gray-500 py-8">
                        <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No recent activity</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Recent Logins Section Component
function RecentLoginsSection({ recentLogins, onNavigate }) {
    return (
        <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5 text-[var(--g6-primary)]" />
                    <h3 className="font-bold text-lg">Recent Logins</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={() => onNavigate?.('admins')}>
                    View All
                </Button>
            </div>
            <div className="space-y-2">
                {recentLogins.length > 0 ? (
                    recentLogins.map((login, index) => (
                        <RecentLoginItem key={index} login={login} />
                    ))
                ) : (
                    <div className="text-center text-gray-500 py-8">
                        <UserCheck className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No recent logins</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Quick Actions Section Component
function QuickActionsSection({ onNavigate }) {
    return (
        <div className="bg-gradient-to-br from-[var(--g6-secondary)] to-[var(--g6-secondary-dark)] rounded-xl p-6 text-white">
            <h3 className="font-bold text-lg mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    onClick={() => onNavigate?.('tenants')}
                >
                    <Building2 className="h-4 w-4 mr-2" />
                    Manage Tenants
                </Button>
                <Button
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    onClick={() => onNavigate?.('admins')}
                >
                    <Users className="h-4 w-4 mr-2" />
                    Manage Admins
                </Button>
                <Button
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    onClick={() => onNavigate?.('settings')}
                >
                    <Activity className="h-4 w-4 mr-2" />
                    Platform Settings
                </Button>
                <Button
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    View Reports
                </Button>
            </div>
        </div>
    );
}

function EnhancedDashboard({ onNavigate }) {
    const [darkMode, setDarkMode] = useState(false);
    const [stats, setStats] = useState(null);
    const [systemHealth, setSystemHealth] = useState([]);
    const [recentActivities, setRecentActivities] = useState([]);
    const [recentLogins, setRecentLogins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [overallHealthStatus, setOverallHealthStatus] = useState('healthy');

    // Create fetch functions using custom hooks
    const fetchStats = useFetchStats(setStats);
    const fetchSystemHealth = useFetchSystemHealth(setSystemHealth, setOverallHealthStatus);
    const fetchRecentLogins = useFetchRecentLogins(setRecentLogins);
    const fetchActivityTimeline = useFetchActivityTimeline(setRecentActivities);

    const fetchAllData = async () => {
        await Promise.all([
            fetchStats(),
            fetchSystemHealth(),
            fetchRecentLogins(),
            fetchActivityTimeline()
        ]);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await fetchAllData();
            } catch (error) {
                logger.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await fetchAllData();
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <div className={cn("min-h-screen p-6", darkMode && "dark bg-gray-900")}>
            <div className="max-w-7xl mx-auto space-y-6">
                <DashboardHeader 
                    darkMode={darkMode}
                    setDarkMode={setDarkMode}
                    refreshing={refreshing}
                    onRefresh={handleRefresh}
                />

                <StatsGrid stats={stats} />

                <SystemHealthSection 
                    systemHealth={systemHealth}
                    overallHealthStatus={overallHealthStatus}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ActivityTimelineSection recentActivities={recentActivities} />
                    <RecentLoginsSection recentLogins={recentLogins} onNavigate={onNavigate} />
                </div>

                <QuickActionsSection onNavigate={onNavigate} />
            </div>
        </div>
    );
}

export default EnhancedDashboard;